import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import mapboxgl from 'mapbox-gl'
import { Icon } from '../../atoms/Icon'
import { Box } from '../../atoms/Box'
import { ImageGallery } from '../../molecules/ImageGallery'
import { MetricsDisplay } from '../../molecules/MetricsDisplay'
import { FeaturesList } from '../../molecules/FeaturesList'
import { ContactCard } from '../ContactCard'
import { PlatformsCard } from '../PlatformsCard'
import { PropertyDetails } from '../../molecules/PropertyDetails'
import { Section } from '../../atoms/Section'
import { SectionHeaderWithIcon as SectionHeader } from '../../molecules/SectionHeaderWithIcon'
import { iconByPortal } from '../../../utils/portal'
import { formatFeatures } from '../../../utils/features'
import { parseWKTPoint } from '../../../utils/geo'
import { MAPBOX_TOKEN } from '../../../config/constants'
import DOMPurify from 'dompurify'
import {
  getListingContactRole,
  getListingContactType,
  getListingOnlineDuration,
  getListingOnlineDurationLabel,
} from './ListingDetailContent.utils'
import { ListingDetailContentProps, ListingDetailTranslations } from './ListingDetail.types'
import { getLocalizedDescription, getLocalizedTitle, SupportedLanguage } from '@popety-io/types'

const defaultTranslations: ListingDetailTranslations = {
  loading: 'Loading listing details...',
  error: 'Error',
  published: 'Published',
  status: 'Status',
  onlineFor: 'Online for',
  onlineDuring: 'Online during',
  active: 'Active',
  expired: 'Expired',
  rooms: 'Rooms',
  livingArea: 'Living Area',
  landArea: 'Land Area',
  bathrooms: 'Bathrooms',
  built: 'Built',
  description: 'Description',
  propertyDetails: 'Property Details',
  featuresAmenities: 'Features & Amenities',
  propertyAgents: 'Property Agents',
  listedOnPortals: 'Listed on Portals',
  priceHistory: 'Price History',
  propertyType: 'Property Type',
  constructionYear: 'Construction Year',
  newConstruction: 'New Construction',
  yes: 'Yes',
  no: 'No',
  floor: 'Floor',
  available: 'Available',
  findAddress: 'Find Address',
  findExactAddress: 'Find the exact address',
  unlinkAddress: 'Unlink address',
  viewOnExplore: 'View on Explore',
  viewInLandAnalysis: 'View in Land Analysis',
  estimate: 'Estimate',
  addToFavorites: 'Add to favorites',
  property: 'Property',
  house: 'House',
  addressNotSpecified: 'Address not specified',
  immediately: 'Immediately',
  unknownAgent: 'Unknown Agent',
  privateVendor: 'Private',
  professionalVendor: 'Professional',
  day: 'day',
  days: 'days',
  approximateLocation: 'Approximate location',
  priceIncrease: 'increase',
  priceDecrease: 'decrease',
  fromPrevious: 'from previous',
}

import {
  ContentWrapper,
  HeroSection,
  QuickInfo,
  QuickInfoTop,
  QuickInfoLeft,
  ListingMetaCard,
  ListingMetaItem,
  ListingMetaLabel,
  ListingMetaValue,
  StatusBadge,
  PropertyTitle,
  PropertyAddress,
  UserLinkedAddress,
  UnlinkButton,
  PriceSection,
  MainPrice,
  PricePerMeter,
  MainContent,
  LeftColumn,
  RightColumn,
  Description,
  MapPreview,
  MapOverlay,
  FindAddressButton,
  ApproximateLocationBadge,
  LoadingState,
  ErrorState,
  PriceHistorySection,
  PriceHistoryHeader,
  PriceHistoryTitle,
  PriceChangeIndicator,
  PriceHistoryTimeline,
  PriceHistoryItem,
  PriceHistoryItemHeader,
  PriceHistoryPrice,
  PriceHistoryDate,
  PriceHistoryChange,
} from './ListingDetail.styles'
import { PrintModeWrapper, PrintHeroImage, GalleryWrapper } from './ListingDetailContent.styles'

const sanitizeHtml = (html: string) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'span',
      'div',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  })
}

const truncateText = (html: string, maxChars: number): string => {
  let text = html.replace(/<[^>]*>/g, ' ')
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
  text = text.replace(/\s+/g, ' ').trim()
  if (text.length > maxChars) {
    return text.substring(0, maxChars).trimEnd() + '...'
  }
  return text
}

export const ListingDetailContent: React.FC<ListingDetailContentProps> = ({
  listing,
  loading = false,
  error = null,
  images = [],
  hasImages = false,
  currentImageIndex = 0,
  goToImage,
  getImageUrl,
  formatPrice,
  formatArea,
  formatRooms,
  formatNumber,
  formatDate,
  translations: propTranslations,
  language = 'en',
  linkedLand,
  onUnlink,
  featureTranslations,
  onFindAddress,
  onViewAgentListings,
  printMode = false,
  hideContactCard = false,
  hidePortals = false,
  hideMap = false,
}) => {
  const translations = { ...defaultTranslations, ...propTranslations }
  const validLanguage: SupportedLanguage = ['en', 'fr', 'de', 'it'].includes(language)
    ? (language as SupportedLanguage)
    : 'en'
  const theme = useTheme()

  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || !listing) return

    let coordinates: [number, number] | null = null
    let isLinkedLocation = false

    if (linkedLand?.geo_center) {
      coordinates = [linkedLand.geo_center.lon, linkedLand.geo_center.lat]
      isLinkedLocation = true
    } else if (listing.geo_center_popety) {
      coordinates = parseWKTPoint(listing.geo_center_popety)
    } else if (listing.geo_center) {
      coordinates = parseWKTPoint(listing.geo_center)
    }

    if (!coordinates) return

    const container = mapContainerRef.current
    const coords = coordinates
    const linked = isLinkedLocation

    const initMap = () => {
      if (!container || mapRef.current) return

      mapboxgl.accessToken = MAPBOX_TOKEN

      const map = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: coords,
        zoom: 15,
        interactive: !printMode,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
      })

      mapRef.current = map

      const marker = new mapboxgl.Marker({
        color: linked ? '#F59E0B' : '#0294df',
      })
        .setLngLat(coords)
        .addTo(map)

      markerRef.current = marker
    }

    // Delay init to ensure the container is laid out and visible
    const timer = requestAnimationFrame(() => {
      initMap()
    })

    return () => {
      cancelAnimationFrame(timer)
      if (markerRef.current) {
        markerRef.current.remove()
        markerRef.current = null
      }
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [listing, linkedLand, printMode])

  if (loading) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <LoadingState>{translations.loading}</LoadingState>
      </PrintModeWrapper>
    )
  }

  if (error) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <ErrorState>
          {translations.error}: {error}
        </ErrorState>
      </PrintModeWrapper>
    )
  }

  if (!listing) {
    return null
  }

  const hasKnownContact = Boolean(
    listing.advertiser_name ||
    listing.broker_info?.contact_names?.some((contact: any) => contact.name)
  )
  const onlineDuration = getListingOnlineDuration(listing, translations)

  return (
    <PrintModeWrapper $printMode={printMode}>
      {/* Hero Image Section */}
      {hasImages && (
        <HeroSection>
          {printMode && (
            <PrintHeroImage
              src={getImageUrl?.(images[currentImageIndex]) || images[currentImageIndex]}
              alt="Property"
            />
          )}
          <GalleryWrapper $printMode={printMode}>
            <ImageGallery
              images={images.map((img) => getImageUrl?.(img) || img)}
              currentIndex={currentImageIndex}
              onIndexChange={goToImage}
              height="100%"
            />
          </GalleryWrapper>
        </HeroSection>
      )}

      <ContentWrapper>
        {/* Quick Info Section */}
        <QuickInfo>
          <QuickInfoTop>
            <QuickInfoLeft>
              <PropertyTitle>
                {getLocalizedTitle(listing, validLanguage) ||
                  getLocalizedDescription(listing, validLanguage) ||
                  ''}
              </PropertyTitle>

              {linkedLand?.address ? (
                <UserLinkedAddress>
                  <Icon name="location" size="small" />
                  <span>{linkedLand.address}</span>
                  {onUnlink && !printMode && (
                    <UnlinkButton
                      onClick={onUnlink}
                      title={translations.unlinkAddress}
                      aria-label={translations.unlinkAddress}
                    >
                      <Icon name="x" size="small" />
                    </UnlinkButton>
                  )}
                </UserLinkedAddress>
              ) : (
                <PropertyAddress>
                  <Icon name="location" size="small" />
                  {listing.address || listing.street || translations.addressNotSpecified}
                  {listing.postal_code &&
                    listing.city_name &&
                    `, ${listing.postal_code} ${listing.city_name}`}
                </PropertyAddress>
              )}
              <PriceSection>
                <MainPrice>{formatPrice?.(listing.price) || '—'}</MainPrice>
                {listing.price_per_square_meter && (
                  <PricePerMeter>
                    {formatPrice?.(listing.price_per_square_meter) || '—'} / m²
                  </PricePerMeter>
                )}
                <span style={{ color: 'white' }}>{listing.id}</span>
              </PriceSection>
            </QuickInfoLeft>

            {/* Listing Meta */}
            <ListingMetaCard>
              <ListingMetaItem>
                <ListingMetaLabel>{translations.published}</ListingMetaLabel>
                <ListingMetaValue>
                  {formatDate?.(listing.listing_timestamp) || '—'}
                </ListingMetaValue>
              </ListingMetaItem>

              <ListingMetaItem>
                <ListingMetaLabel>{translations.status}</ListingMetaLabel>
                <StatusBadge
                  variant={listing.ad_status !== false ? 'success' : 'danger'}
                  size="small"
                >
                  {listing.ad_status !== false ? translations.active : translations.expired}
                </StatusBadge>
              </ListingMetaItem>

              {onlineDuration && (
                <ListingMetaItem>
                  <ListingMetaLabel>
                    {getListingOnlineDurationLabel(listing, translations)}
                  </ListingMetaLabel>
                  <ListingMetaValue>{onlineDuration}</ListingMetaValue>
                </ListingMetaItem>
              )}
            </ListingMetaCard>
          </QuickInfoTop>

          <MetricsDisplay
            metrics={[
              {
                value: formatRooms?.(listing.rooms_nb) || '—',
                label: translations.rooms,
              },
              {
                value: formatArea?.(listing.area) || '—',
                label: translations.livingArea,
              },
              {
                value: formatArea?.(listing.land_area) || '—',
                label: translations.landArea,
              },
              {
                value: formatNumber?.(listing.bathroom_nb) || '—',
                label: translations.bathrooms,
              },
              {
                value: formatNumber?.(listing.construction_year) || '—',
                label: translations.built,
              },
            ]}
            columns={5}
          />
        </QuickInfo>

        {/* Price History - only show if there are actual price changes */}
        {listing.price_history &&
          listing.price_history.length > 1 &&
          new Set(listing.price_history.map((h: any) => Math.round(h.price))).size > 1 && (
            <PriceHistorySection>
              <PriceHistoryHeader
                $expanded={priceHistoryOpen}
                onClick={() => setPriceHistoryOpen((v) => !v)}
              >
                <PriceHistoryTitle>
                  <Icon name="chart-line" size="small" />
                  {translations.priceHistory}
                  <Icon name={priceHistoryOpen ? 'chevron-up' : 'chevron-down'} size="xs" />
                </PriceHistoryTitle>
                {(() => {
                  const history = [...listing.price_history].sort(
                    (a: any, b: any) =>
                      new Date(b.listing_timestamp).getTime() -
                      new Date(a.listing_timestamp).getTime()
                  )
                  const currentPrice = history[0]?.price
                  const originalPrice = history[history.length - 1]?.price
                  const hasValidPrices = currentPrice && originalPrice && originalPrice !== 0
                  if (hasValidPrices) {
                    const change = ((currentPrice - originalPrice) / originalPrice) * 100
                    if (change === 0) return null
                    return (
                      <PriceChangeIndicator $positive={change > 0}>
                        {change > 0 ? '+' : ''}
                        {change.toFixed(1)}%{' '}
                        {change > 0 ? translations.priceIncrease : translations.priceDecrease}
                      </PriceChangeIndicator>
                    )
                  }
                  return null
                })()}
              </PriceHistoryHeader>
              {priceHistoryOpen && (
                <PriceHistoryTimeline>
                  {(() => {
                    const sortedHistory = [...listing.price_history]
                      .sort(
                        (a: any, b: any) =>
                          new Date(b.listing_timestamp).getTime() -
                          new Date(a.listing_timestamp).getTime()
                      )
                      .filter((item: any, index: number, arr: any[]) => {
                        if (index === 0) return true
                        const newerItem = arr[index - 1]
                        return Math.round(item.price) !== Math.round(newerItem.price)
                      })

                    return sortedHistory.map((item: any, index: number, arr: any[]) => {
                      const prevItem = arr[index + 1]
                      const priceChange =
                        prevItem && prevItem.price !== 0
                          ? ((item.price - prevItem.price) / prevItem.price) * 100
                          : null

                      return (
                        <PriceHistoryItem
                          key={`${item.listing_timestamp}-${index}`}
                          $isFirst={index === 0}
                        >
                          <PriceHistoryItemHeader>
                            <PriceHistoryPrice $isFirst={index === 0}>
                              {formatPrice?.(item.price) || '—'}
                            </PriceHistoryPrice>
                            <PriceHistoryDate>
                              {formatDate?.(item.listing_timestamp) || '—'}
                            </PriceHistoryDate>
                          </PriceHistoryItemHeader>
                          {priceChange !== null && priceChange !== 0 && (
                            <PriceHistoryChange $increase={priceChange > 0}>
                              <Icon name={priceChange > 0 ? 'arrow-up' : 'arrow-down'} size="xs" />
                              {priceChange > 0 ? '+' : ''}
                              {priceChange.toFixed(1)}% {translations.fromPrevious}
                            </PriceHistoryChange>
                          )}
                        </PriceHistoryItem>
                      )
                    })
                  })()}
                </PriceHistoryTimeline>
              )}
            </PriceHistorySection>
          )}

        {/* Main Content Grid */}
        <MainContent>
          <LeftColumn>
            {/* Description Section */}
            {getLocalizedDescription(listing, validLanguage) && (
              <Section>
                <SectionHeader icon="document-text">{translations.description}</SectionHeader>
                {printMode ? (
                  <Description>
                    {truncateText(getLocalizedDescription(listing, validLanguage), 1800)}
                  </Description>
                ) : (
                  <Description
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(getLocalizedDescription(listing, validLanguage)),
                    }}
                  />
                )}
              </Section>
            )}

            {/* Property Details */}
            <Section>
              <SectionHeader icon="info">{translations.propertyDetails}</SectionHeader>
              <PropertyDetails
                details={[
                  {
                    label: translations.propertyType,
                    value: listing.property_category || listing.property_type || translations.house,
                  },
                  {
                    label: translations.constructionYear,
                    value: formatNumber?.(listing.construction_year) || '—',
                  },
                  {
                    label: translations.newConstruction || 'New Construction',
                    value: listing.new_construction ? translations.yes : translations.no,
                  },
                  {
                    label: translations.livingArea,
                    value: formatArea?.(listing.area) || '—',
                  },
                  {
                    label: translations.landArea,
                    value: formatArea?.(listing.land_area) || '—',
                  },
                  {
                    label: translations.rooms,
                    value: formatRooms?.(listing.rooms_nb) || '—',
                  },
                  {
                    label: translations.bathrooms,
                    value: formatNumber?.(listing.bathroom_nb) || '—',
                  },
                  {
                    label: translations.floor,
                    value: formatNumber?.(listing.floor) || '—',
                  },
                  {
                    label: translations.available,
                    value: listing.AvailableDate || translations.immediately,
                  },
                ]}
              />
            </Section>

            {/* Features Section */}
            {listing.features && listing.features.length > 0 && (
              <Section>
                <SectionHeader icon="star">{translations.featuresAmenities}</SectionHeader>
                <FeaturesList
                  features={formatFeatures(listing.features, featureTranslations)}
                  containerProps={{
                    background: true,
                    padding: theme.spacing.xlarge,
                  }}
                />
              </Section>
            )}
          </LeftColumn>

          <RightColumn>
            {/* Map Preview (hidden in print mode or when hideMap is true) */}
            {!printMode && !hideMap && (
              <MapPreview>
                <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
                {listing &&
                  !linkedLand &&
                  (!listing.exact_location || (!listing.address && !listing.street)) &&
                  (onFindAddress ? (
                    <MapOverlay>
                      <FindAddressButton onClick={onFindAddress}>
                        <Icon name="location" size="small" />
                        {translations.findExactAddress}
                      </FindAddressButton>
                    </MapOverlay>
                  ) : (
                    <ApproximateLocationBadge>
                      <Icon name="alert-triangle" size="xs" />
                      {translations.approximateLocation}
                    </ApproximateLocationBadge>
                  ))}
              </MapPreview>
            )}

            {/* Contact Card */}
            {!hideContactCard && (
              <Box>
                <ContactCard
                  title={translations.propertyAgents}
                  contacts={(() => {
                    if (
                      listing.broker_info?.contact_names &&
                      listing.broker_info.contact_names.length > 0
                    ) {
                      return listing.broker_info.contact_names.map((contact: any) => {
                        const type = getListingContactType(listing.vendor_type, contact.type)
                        const role = getListingContactRole(
                          listing.vendor_type,
                          contact.type,
                          translations
                        )

                        return {
                          name: contact.name,
                          type,
                          role,
                          showRole: Boolean(role),
                        }
                      })
                    }

                    const type = getListingContactType(listing.vendor_type)
                    const role = getListingContactRole(listing.vendor_type, undefined, translations)

                    return [
                      {
                        name: listing.advertiser_name || translations.unknownAgent,
                        type,
                        role,
                        showRole: Boolean(role),
                      },
                    ]
                  })()}
                  mandateType={listing.broker_info?.mandate}
                  onContactClick={
                    onViewAgentListings && !printMode && hasKnownContact
                      ? (contact) => {
                          const agentName = listing.advertiser_name || contact.name
                          if (agentName && agentName !== translations.unknownAgent) {
                            onViewAgentListings(agentName, listing)
                          }
                        }
                      : undefined
                  }
                />
              </Box>
            )}

            {/* Listed on Platforms */}
            {!hidePortals && listing.listing_portals && listing.listing_portals.length > 0 && (
              <Box marginTop="16px">
                <PlatformsCard
                  title={translations.listedOnPortals}
                  platforms={listing.listing_portals.map((portal: any) => ({
                    name: portal.name,
                    url: printMode ? undefined : portal.original_url,
                    active: portal.active,
                    iconUrl: iconByPortal[portal.name]
                      ? `/portal/${iconByPortal[portal.name]}`
                      : undefined,
                  }))}
                />
              </Box>
            )}
          </RightColumn>
        </MainContent>
      </ContentWrapper>
    </PrintModeWrapper>
  )
}
